import {
  createFocusTrap,
} from 'focus-trap';

import {
  idref,
} from '../../utilities/dom.js';

import {
  clamp,
} from '../../utilities/math.js';

import {
  getFocusableItems,
  wrapIndex,
} from './FocusZone.helpers.js';

export class FocusZone extends HTMLElement {

  /**
   * @param {string} tagName
   * @returns {void}
   */
  static define(tagName = 'focus-zone') {
    if ('customElements' in window) {
      customElements.define(tagName, FocusZone);
    }
  }

  /**
   * @returns {string[]}
   */
  static get observedAttributes() {
    return ['hidden'];
  }

  /** @returns {boolean} */
  get loop() {
    return this.hasAttribute('loop');
  }

  /** @param {?boolean} value */
  set loop(value) {
    this.toggleAttribute('loop', !!value);
  }

  /** @returns {boolean} */
  get trapped() {
    return this.hasAttribute('trapped');
  }

  /** @param {?boolean} value */
  set trapped(value) {
    this.toggleAttribute('trapped', !!value);
  }

  /** @returns {HTMLElement[]} */
  get items() {
    return this.#items;
  }

  /** @returns {number} */
  get length() {
    return this.#items.length;
  }

  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {?MutationObserver} */
  #observer = null;

  /** @type {?FocusTrap} */
  #trap = null;

  constructor() {
    super();

    this.childrenChangedCallback = this.childrenChangedCallback.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  /**
   * @returns {void}
   */
  connectedCallback() {
    this.#items = getFocusableItems(this);

    this.#observer = new MutationObserver(this.childrenChangedCallback);

    this.#observer.observe(this, {
      childList: true,
      subtree: true,
    });

    this.addEventListener('keydown', this.handleKeydown);
  }

  /**
   * @returns {void}
   */
  disconnectedCallback() {
    this.removeEventListener('keydown', this.handleKeydown);

    this.#observer?.disconnect();
    this.#trap?.deactivate();

    this.#items.splice(0);

    this.#observer = null;
    this.#trap = null;
  }

  /**
   * @returns {void}
   */
  attributeChangedCallback(attributeName, oldValue, newValue) {
    if (attributeName === 'hidden') {
      if (this.trapped && newValue === null) {
        this.activateTrap();
      } else {
        this.deactivateTrap();
      }
    }
  }

  /**
   * @param {MutationRecord[]} mutations
   * @returns {void}
   */
  childrenChangedCallback(mutations) {
    this.#items = getFocusableItems(this);
  }

  /**
   * @param {number} index
   * @returns {?HTMLElement}
   */
  item(index) {
    const total = this.#items.length;

    if (this.loop) {
      index = wrapIndex(index, total);
    } else {
      index = clamp(0, index, total - 1);
    }

    return this.#items[index] ?? null;
  }

  /**
   * @returns {void}
   */
  activateTrap() {
    this.#trap ??= createFocusTrap(this, {
      allowOutsideClick: true,
      escapeDeactivates: true,
    });
    this.#trap.activate();
  }

  /**
   * @returns {void}
   */
  deactivateTrap() {
    this.#trap?.deactivate();
  }

  /**
   * @param {number} index
   */
  moveToItem(index) {
    this.item(index)?.focus();
  }

  /**
   * @returns {void}
   */
  moveToFirstItem() {
    return this.moveToItem(0);
  }

  /**
   * @returns {void}
   */
  moveToLastItem() {
    return this.moveToItem(this.#items.length - 1);
  }

  /**
   * @param {number} index
   * @returns {boolean}
   */
  moveToInnerZone(index) {
    const item = this.item(index);

    if (! item) {
      return false;
    }

    const open = item.getAttribute('aria-expanded') === 'true';

    if (! open) {
      return false;
    }

    const target = idref(item.getAttribute('aria-controls'));

    if (target instanceof FocusZone) {
      target.moveToFirstItem();
      return true;
    }

    return false;
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const item = /** @type {?HTMLElement} */ (event.target);

    if (! item || ! this.contains(item)) {
      return;
    }

    const index = this.#items.indexOf(item);

    if (index === -1) {
      return;
    }

    const {key} = event;

    if (key === 'ArrowDown' || key === 'ArrowRight') {
      if (item.localName === 'button' && this.moveToInnerZone(index)) {
        event.preventDefault();
      } else {
        this.moveToItem(index + 1);
        event.preventDefault();
      }
    } else if (key === 'ArrowUp' || key === 'ArrowLeft') {
      this.moveToItem(index - 1);
      event.preventDefault();
    } else if (key === 'Home') {
      this.moveToFirstItem();
      event.preventDefault();
    } else if (key === 'End') {
      this.moveToLastItem();
      event.preventDefault();
    }
  }
}